/**
 * Clone-options form domain layer for CloneUnitModal.
 *
 * Translates between the raw ModelCloneOptions schema the
 * GET /sites/{id}/clone/ endpoint returns (server-filtered to the source
 * model's enabled:true entries) and the modal's editable form state, and
 * builds the task-native POST payload from that form state. Pure functions
 * only — no React, no IO.
 *
 * Contains:
 * - parseCloneOptionsResponse — server schema -> editable form state
 * - buildCloneOptionsPayload — form state -> POST /sites/{id}/clone/ body
 */

/**
 * Map the GET /sites/{id}/clone/ options shape to the modal's editable
 * form state.
 *
 * Server shape per option key:
 *   { label, fields: [{field, label}], options?: [{field, label, default}], relays?: [relayNumber] }
 *
 * Form state shape per option key:
 *   { label, enabled, fields: [{field, label, enabled}], options: [{field, label, enabled}],
 *     relays: [{relay, enabled}] | null }
 *
 * `relays` is read from the option's own `relays` array, not derived from
 * `fields`. Option labels are the server's literal strings (legacy
 * behavior — no locale key exists for them yet).
 *
 * @param {object} options - the response's `options` map, keyed by option name.
 * @returns {object} the modal's `cloneOptions` form state, same keys.
 */
export function parseCloneOptionsResponse(options) {
  const cloneOptions = {};

  Object.keys(options).forEach(key => {
    const opt = options[key];

    cloneOptions[key] = {
      label: opt.label,
      enabled: false,
      fields: (opt.fields || []).map(f => ({ field: f.field, label: f.label, enabled: false })),
      options: (opt.options || []).map(o => ({ field: o.field, label: o.label, enabled: o.default || false })),
      relays: opt.relays ? opt.relays.map(relay => ({ relay, enabled: true })) : null,
    };
  });

  return cloneOptions;
}

/**
 * Build the POST /sites/{id}/clone/ `options` body from the modal's form
 * state: the task-native shape per option key, `{enabled, relays: [int],
 * fields: [{field, enabled}], options: [{field, enabled}]}`. The "Clone
 * Options" advanced subsection (`options`) is legacy UI parity — the clone
 * task itself never reads it — but the server accepts and validates it, so
 * it is sent like `fields` rather than dropped.
 *
 * @param {object} cloneOptions - the modal's `cloneOptions` form state, keyed by option name.
 * @returns {object} the request body's `options` map, same keys.
 */
export function buildCloneOptionsPayload(cloneOptions) {
  const payload = {};

  Object.keys(cloneOptions).forEach(key => {
    const opt = cloneOptions[key];
    payload[key] = {
      enabled: opt.enabled,
      fields: opt.fields.map(f => ({ field: f.field, enabled: f.enabled })),
      options: opt.options.map(o => ({ field: o.field, enabled: o.enabled })),
      relays: opt.relays ? opt.relays.filter(r => r.enabled).map(r => r.relay) : [],
    };
  });

  return payload;
}
